import { ApiVersions } from '@gonfalon/openapi/api-versions';
import { useQuery } from '@tanstack/react-query';

import type { AggregationType, Granularity, UsageSeriesList, UsageTabKey } from '../../types';
import { DimensionType } from '../../types';
import { usageTabConfig } from '../constants/usageTabConfig';
import { getDimensionMetadataKey, getGroupByDimensions } from '../utils/getGroupByDimensions';
import { getSortedDimensionTotals } from '../utils/getSortedDimensionTotals';
import { resolveQueryFn } from '../utils/resolveQueryFn';

type UseGroupedUsageTabDataProps = {
  usageTab: UsageTabKey;
  aggregationType: AggregationType;
  fromTimestampUTC: number;
  toTimestampUTC: number;
  dimension?: DimensionType;
  granularity: Granularity;
};

export type GroupedUsageTabData = {
  data?: UsageSeriesList;
  isLoading: boolean;
  isError: boolean;
  sortedTotals: Array<[number, string]> | undefined;
  metadataKey: string;
};

export function useGroupedUsageTabData({
  usageTab,
  aggregationType,
  fromTimestampUTC,
  toTimestampUTC,
  dimension,
  granularity,
}: UseGroupedUsageTabDataProps) {
  const { queryFnName, sdkQueryFnName } = usageTabConfig[usageTab];
  const isV1Sdk = sdkQueryFnName && isV1SdkDimension(dimension);
  const queryFn = resolveQueryFn(isV1Sdk ? sdkQueryFnName : queryFnName);

  const dimensions = dimension ? getGroupByDimensions(usageTab, dimension) : undefined;

  const defaultQuery = {
    from: fromTimestampUTC.toString(),
    to: toTimestampUTC.toString(),
  };

  const v2ApiQueryParams = {
    aggregationType,
    groupBy: dimensions,
    granularity,
  };

  const query = useQuery<UsageSeriesList>({
    ...queryFn({
      apiVersion: ApiVersions.Beta,
      query: isV1Sdk ? defaultQuery : { ...defaultQuery, ...v2ApiQueryParams },
    }),
    enabled: !!dimension,
  });

  const metadataKey = getDimensionMetadataKey(dimension);
  const sortedTotals = query.data
    ? getSortedDimensionTotals({ data: query.data, aggregationType, granularity })
    : undefined;

  return {
    isLoading: query.isLoading,
    isError: query.isError,
    data: query.data,
    sortedTotals,
    metadataKey,
  };
}

function isV1SdkDimension(dimension?: DimensionType) {
  return dimension === DimensionType.SDK_VERSION_V1;
}
